import React, { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CustomerHeroCard,
  LicenseCreditCards,
  AiRecommendations,
  OrderHistoryCard,
  ProductAffinityCard,
  ActivityTimelineCard,
} from './components';
import {
  CustomerHero,
  LicenseStatus,
  CreditSummary,
  AiRecommendation,
  OrderHistoryEntry,
  ProductAffinity,
  ActivityEntry,
} from './customerDetailsTypes';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useCustomersStore } from '../../../store/customersStore';
import { Customer } from '../../../services/customersApi';
import styles from './SalesRepCustomerDetails.module.css';

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function buildHero(c: Customer): CustomerHero {
  return {
    name: c.store_name || c.full_name,
    contact: c.full_name,
    phone: c.contact_phone || '—',
    email: c.email || undefined,
    location: [c.address_line1, c.city, c.state, c.zip_code].filter(Boolean).join(', ') || '—',
    creditStatus: c.metrics.outstanding_balance > 0 ? 'Outstanding' : 'Good',
    lastVisit: relativeTime(c.metrics.last_order_at),
    isActive: c.is_active && c.customer_status !== 'suspended',
  };
}

function buildCredit(c: Customer): CreditSummary {
  // Backend doesn't yet expose a configured credit limit per customer.
  // Use a derived ceiling so the card still renders meaningfully.
  const ceiling = Math.max(c.metrics.total_revenue * 0.5, c.metrics.outstanding_balance * 1.5, 1);
  const utilizationPct = Math.min(100, Math.round((c.metrics.outstanding_balance / ceiling) * 100));
  return {
    creditLimit: `$${Math.round(ceiling).toLocaleString()}`,
    outstanding: `$${Math.round(c.metrics.outstanding_balance).toLocaleString()}`,
    utilizationPct,
    withinLimit: utilizationPct < 90,
  };
}

const PLACEHOLDER_LICENSE: LicenseStatus = { status: 'Valid', expiryDate: '—' };
const EMPTY_AI: AiRecommendation[] = [];
const EMPTY_ORDERS: OrderHistoryEntry[] = [];
const EMPTY_AFFINITY: ProductAffinity[] = [];
const EMPTY_TIMELINE: ActivityEntry[] = [];

const SalesRepCustomerDetails: React.FC = () => {
  const [params] = useSearchParams();
  const id = params.get('id');

  const item = useCustomersStore((s) => s.item);
  const loading = useCustomersStore((s) => s.loading);
  const error = useCustomersStore((s) => s.error);
  const fetchOne = useCustomersStore((s) => s.fetchOne);

  useEffect(() => {
    if (id) {
      fetchOne(id).catch(() => undefined);
    }
  }, [id, fetchOne]);

  const hero = useMemo(() => (item ? buildHero(item) : null), [item]);
  const credit = useMemo(() => (item ? buildCredit(item) : null), [item]);

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <Link to="/customers" className={styles.backLink}>
          ← Back to Customers
        </Link>
        <Link to={`/new-order${id ? `?customerId=${id}` : ''}`} className={styles.createBtn}>
          + Create Order
        </Link>
      </div>

      <AsyncBoundary
        loading={loading && !item}
        error={error}
        empty={!loading && !item}
        emptyMessage={id ? 'Customer not found' : 'No customer selected'}
        onRetry={id ? () => fetchOne(id) : undefined}
      >
        {hero && credit && (
          <>
            <CustomerHeroCard hero={hero} />
            <LicenseCreditCards license={PLACEHOLDER_LICENSE} credit={credit} />
            <AiRecommendations items={EMPTY_AI} />
            <div className={styles.midRow}>
              <OrderHistoryCard entries={EMPTY_ORDERS} />
              <ProductAffinityCard items={EMPTY_AFFINITY} />
            </div>
            <ActivityTimelineCard entries={EMPTY_TIMELINE} />
          </>
        )}
      </AsyncBoundary>
    </div>
  );
};

export const SalesRepCustomerDetailsPage: React.FC = () => {
  return <SalesRepCustomerDetails />;
};

export { SalesRepCustomerDetails };
export default SalesRepCustomerDetails;
